import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import { 
  Loader2, 
  CheckCircle, 
  AlertTriangle, 
  Building2,
  Lock,
  Mail,
  User,
  Eye,
  EyeOff
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const STERILE_MATE_LOGO = "https://sterilemate.com/wp-content/uploads/2024/05/cropped-Sterile-Mate-2024-V3-e1728688787498-300x159.png";

// Digital Isaac Avatar (Option 2 - with data screens)
const DIGITAL_ISAAC = "https://static.prod-images.emergentagent.com/jobs/0bc119bf-ae15-43e9-9f13-14b53e074c94/images/f6367888bb9bb3d7bdad4fd836f9d151ec32ceb848a5b100c0a41679671f3cb1.png";

export default function ContractorRegisterPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const token = searchParams.get("token");

  const [validating, setValidating] = useState(true);
  const [inviteData, setInviteData] = useState(null);
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (token) {
      validateToken();
    } else {
      setError("No invitation token provided");
      setValidating(false);
    }
  }, [token]);

  // When no token is present, this page is being hit as the public
  // self-service registration URL. Redirect to the unified self-register
  // form which collects the full profile + onboarding documents.
  // (Must come AFTER hook declarations to comply with rules-of-hooks.)
  if (!token) {
    return <Navigate to="/register?type=contractor" replace />;
  }

  const validateToken = async () => {
    try {
      const response = await fetch(`${API}/contractors/invite/validate/${token}`);
      if (response.ok) {
        const data = await response.json();
        setInviteData(data);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || "Invalid invitation");
      }
    } catch (error) {
      setError("Failed to validate invitation");
    } finally {
      setValidating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API}/contractors/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          invite_token: token,
          password: password
        })
      });

      if (response.ok) {
        const data = await response.json();
        setSuccess(true);
        toast.success("Account created successfully!");
        
        // Auto-login with the returned token
        setTimeout(() => {
          login(data.token, data.user);
          navigate("/admin/projects");
        }, 2000);
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || "Failed to create account");
      }
    } catch (error) {
      toast.error("Error creating account");
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (validating) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-500 mx-auto mb-4" />
          <p className="text-slate-400">Validating invitation...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-slate-800 border border-slate-700 rounded-2xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-8 h-8 text-red-400" />
          </div>
          <h1 className="text-xl font-bold text-white mb-2">Invalid Invitation</h1>
          <p className="text-slate-400 mb-6">{error}</p>
          <Link to="/">
            <Button className="bg-blue-600 hover:bg-blue-700">
              Return Home
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-slate-800 border border-slate-700 rounded-2xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-emerald-400" />
          </div>
          <h1 className="text-xl font-bold text-white mb-2">Account Created!</h1>
          <p className="text-slate-400 mb-4">Welcome to Sterile Mate, {inviteData?.name}!</p>
          <p className="text-slate-500 text-sm">Redirecting to your dashboard...</p>
          <Loader2 className="w-6 h-6 animate-spin text-blue-500 mx-auto mt-4" />
        </div>
      </div>
    );
  }

  // Registration form
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-5xl flex items-center gap-8">
        {/* Left side - Digital Isaac */}
        <div className="hidden lg:flex flex-col items-center justify-center flex-1">
          <div className="relative">
            {/* Glow effect */}
            <div className="absolute inset-0 bg-gradient-radial from-cyan-500/20 via-blue-500/10 to-transparent blur-3xl scale-110" />
            
            {/* Isaac Image */}
            <img 
              src={DIGITAL_ISAAC}
              alt="Isaac - Your Healthcare Assistant"
              className="relative z-10 w-full max-w-sm h-auto object-contain"
              style={{ 
                filter: 'drop-shadow(0 0 40px rgba(96, 165, 250, 0.3))',
                animation: 'float 6s ease-in-out infinite'
              }}
            />
          </div>
          
          {/* Welcome text */}
          <div className="text-center mt-6">
            <h2 className="text-2xl font-bold text-white mb-2">Welcome, Contractor!</h2>
            <p className="text-slate-400 max-w-sm">Isaac is here to help manage your projects and connect you with healthcare facilities.</p>
          </div>
          
          <style>{`
            @keyframes float {
              0%, 100% { transform: translateY(0px); }
              50% { transform: translateY(-15px); }
            }
          `}</style>
        </div>
        
        {/* Right side - Form */}
        <div className="bg-slate-800 border border-slate-700 rounded-2xl p-8 max-w-md w-full">
          {/* Logo */}
          <div className="text-center mb-8">
            <img 
              src={STERILE_MATE_LOGO} 
              alt="Sterile Mate" 
              className="h-16 mx-auto mb-4"
            />
            <h1 className="text-2xl font-bold text-white">Welcome to Sterile Mate</h1>
            <p className="text-slate-400 mt-2">Complete your account setup</p>
          </div>

          {/* Invitation Info */}
          <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-white font-medium">{inviteData?.org_name}</p>
                <p className="text-slate-400 text-sm">Organization</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="flex items-center gap-2 text-slate-400">
                <User className="w-4 h-4" />
                <span>{inviteData?.name}</span>
              </div>
              <div className="flex items-center gap-2 text-slate-400">
                <Mail className="w-4 h-4" />
                <span className="truncate">{inviteData?.email}</span>
              </div>
            </div>
          </div>

          {/* Registration Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Create Password</Label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  className="bg-slate-900 border-slate-700 text-white pr-10"
                  placeholder="At least 8 characters"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Confirm Password</Label>
              <Input
                type={showPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="bg-slate-900 border-slate-700 text-white"
                placeholder="Re-enter password"
            />
          </div>

          <Button
            type="submit"
            disabled={submitting}
            className="w-full bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600"
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Lock className="w-4 h-4 mr-2" />
            )}
            Create Account
          </Button>
        </form>

          {/* Footer */}
          <p className="text-center text-xs text-slate-500 mt-6">
            By creating an account, you agree to maintain HIPAA compliance and confidentiality of all project data.
          </p>
        </div>
      </div>
    </div>
  );
}
